// type FN<A extends any[], R> = (...args: A) => R;
// type Inputs<F extends FN<any, any>> = Parameters<F>;

import { CompareUtil, EqualsFunction, EqualsFunctionOption } from "@base/util/CompareUtil";
import { AnyFunction, PrimitiveType } from "./TypeUtil";

const nothingSym = Symbol("empty");

export type ICacheUsages = Record<string, number>;

export interface ICacheFunctionStats {
  recomputations: number;
  argumentsEqual: number;
  resultEqual: number;
}
export interface ICacheItem<R = any, I extends readonly any[] = readonly any[]> {
  inputs: I;
  result: R;
  stats: ICacheFunctionStats;
}

export type InputsEqualFn = (prev: readonly any[], next: readonly any[]) => boolean

function areInputsEqual(equalityFn: EqualsFunction<any>, prev: readonly any[], next: readonly any[]): boolean {
  if (!prev || !next || prev.length !== next.length) {
    return false
  }

  const length = prev.length
  for (let i = 0; i < length; i++) {
    if (!equalityFn(prev[i], next[i])) {
      return false
    }
  }

  return true
}

export function inputsRefEqual(prev: readonly any[], next: readonly any[]): boolean {
  return areInputsEqual(CompareUtil.refEqual, prev, next);
}

export function inputsShallowEqual(prev: readonly any[], next: readonly any[]): boolean {
  return areInputsEqual(CompareUtil.shallowEqual, prev, next);
}

export type InputsEqualFnOptions = InputsEqualFn | "ref" | "shallow"
export function getInputEqualFunction(inputEqualFunction: InputsEqualFnOptions): InputsEqualFn {
  switch (inputEqualFunction) {
    case "ref":
      return inputsRefEqual;
    case "shallow":
      return inputsShallowEqual;
  }
  return inputEqualFunction;
}


export type CacheStorageNode = Map<any, CacheStorageNode | ICacheItem>;
const cacheTree: CacheStorageNode = new Map<any, CacheStorageNode>();
export class CacheUtil {

  private static logNode(node: CacheStorageNode): void {
    for (const k of node.keys()) {
      const childNode = node.get(k);
      if ((childNode as any).keys) {
        console.group(k);
        CacheUtil.logNode(childNode as CacheStorageNode);
      } else {
        const ci = childNode as ICacheItem;
        console.groupCollapsed(k, ci.stats);
        console.log("Stats", ci.stats);
        console.log("Inputs", ci.inputs);
        console.log("Results", ci.result);
      }
      console.groupEnd();
    }
  }

  static log(): void {
    console.log("CACHE")
    console.log("-----------------------")

    CacheUtil.logNode(cacheTree);
  }

  private static calcCacheNode(cacheIds: any[]): CacheStorageNode {
    let cacheNode = cacheTree;
    for (let i = 0; i < cacheIds.length - 1; i++) {
      const cid = cacheIds[i];
      const node = cacheNode.get(cid);
      if (node) {
        cacheNode = node as CacheStorageNode;
      } else {
        const newNode = new Map<any, CacheStorageNode>();
        cacheNode.set(cid, newNode);
        cacheNode = newNode;
      }
    }
    return cacheNode;
  }

  // static functionName(func: Function): string {
  //   // Match:
  //   // - ^          the beginning of the string
  //   // - function   the word 'function'
  //   // - \s+        at least some white space
  //   // - ([\w\$]+)  capture one or more valid JavaScript identifier characters
  //   // - \s*        optionally followed by white space (in theory there won't be any here,
  //   //              so if performance is an issue this can be omitted[1]
  //   // - \(         followed by an opening brace
  //   //
  //   const name = func.toString();
  //   const result = /^function\s+([\w$]+)\s*\(/.exec(name)

  //   return result ? result[1] : '' // for an anonymous function there won't be a match
  // }

  // static ignoreCacheGlobal = false && isDebug();
  static ignoreMemo = false; // isDebug();
  static ignoreCacheInput = false; // isDebug();

  static memo<F extends AnyFunction>(fn: F, name: string, options?: {
    resultEqualFn?: EqualsFunctionOption;
    inputsEqualFn?: InputsEqualFnOptions | undefined;
    idsFromArgs?: ((...args: Parameters<F>) => (PrimitiveType[])) | number | PrimitiveType[] | "all";
    inputsFromArgs?: (...args: Parameters<F>) => any[];
  }): F {

    if (CacheUtil.ignoreMemo)
      return fn;

    const { resultEqualFn, inputsEqualFn, idsFromArgs, inputsFromArgs } = options || {};


    let inputsFn: ((...args: Parameters<F>) => any[]) | undefined = inputsFromArgs;

    const functionName = name; // CacheUtil.functionName(fn);

    let idsFn: ((...args: Parameters<F>) => any[]) | any[];
    if (typeof idsFromArgs === "number") {
      idsFn = (...args: Parameters<F>) => [...args.slice(0, idsFromArgs), functionName]; // first few parameters
      if (!inputsFn)
        inputsFn = (...args: Parameters<F>) => [...args.slice(idsFromArgs), functionName]; // last few parameters
    } else if (Array.isArray(idsFromArgs)) {
      idsFn = [...idsFromArgs, functionName];
    } else if (idsFromArgs === "all") {
      idsFn = [...idsFromArgs, functionName]
    } else if (idsFromArgs) {
      idsFn = idsFromArgs;
    } else {
      idsFn = [functionName];
    }

    return ((...args: any): any => {
      const ids = Array.isArray(idsFn) ? idsFn : [...idsFn(...args), functionName];
      return CacheUtil.callCached(fn, args, {
        ids,
        inputs: inputsFn ? inputsFn(...args) : undefined,
        resultEqualFn: CompareUtil.getEqualsFunction(resultEqualFn || "ref"),
        inputsEqualFn: inputsEqualFn ? getInputEqualFunction(inputsEqualFn) : undefined,
      })
    }) as F
  }

  static callCached<A extends readonly any[], R>(fn: (...args: A) => R, args: A, cacheOptions: {
    ids: any[];
    inputs?: any[];
    inputsEqualFn?: InputsEqualFn | undefined;
    resultEqualFn?: EqualsFunction<any>;
  }): R {
    const { ids: cacheIds, inputs, inputsEqualFn = inputsRefEqual, resultEqualFn = CompareUtil.refEqual } = cacheOptions;

    const cacheNode = CacheUtil.calcCacheNode(cacheIds);
    const id = cacheIds[cacheIds.length - 1];
    if (!cacheNode || !cacheNode.get) {
      throw new Error("Empty cachenode");
    }

    const cacheItem = CacheUtil.getOrCreateCacheItem(cacheNode, id);
    return CacheUtil.callCachedWithItem(fn, args, {
      cacheItem,
      inputs,
      inputsEqualFn,
      resultEqualFn
    });
  }

  static createEmptyCacheItem(): ICacheItem {
    const cacheItem: ICacheItem = {
      inputs: [],
      result: nothingSym,
      stats: {
        recomputations: 0,
        argumentsEqual: 0,
        resultEqual: 0
      }
    }
    return cacheItem;
  }

  static getOrCreateCacheItem(cache: CacheStorageNode, id: string): ICacheItem {
    const cacheItem = cache.get(id);
    if (cacheItem)
      return cacheItem as ICacheItem;
    const newCacheItem = CacheUtil.createEmptyCacheItem();
    cache.set(id, newCacheItem);
    return newCacheItem;
  }

  static callCachedWithItem<A extends readonly any[], R>(fn: (...args: A) => R, args: A, cacheOptions: {
    cacheItem: ICacheItem;
    inputs?: any[];
    inputsEqualFn?: InputsEqualFn | undefined;
    resultEqualFn?: EqualsFunction<any>;
  }): R {
    const { cacheItem, inputs = args, inputsEqualFn = inputsRefEqual, resultEqualFn = CompareUtil.refEqual } = cacheOptions;

    // if cacheItem is not empty => check if for same inputs
    if (!CacheUtil.ignoreCacheInput && cacheItem.result !== nothingSym) {
      const ret = CacheUtil.checkInputs(cacheItem, inputs, inputsEqualFn);
      if (ret !== nothingSym) {
        return ret;
      }
    }

    // calculate function result
    const result = fn(...args)

    // update stats
    if (cacheItem.stats)
      cacheItem.stats.recomputations++;

    // check if result is equal => keep result instance (for optimzed immutable rendering)
    cacheItem.inputs = inputs;
    if (resultEqualFn(cacheItem.result, result)) {
      cacheItem.stats.resultEqual++;
      return cacheItem.result;
    }

    // return new result otherwise
    cacheItem.result = result;
    return result;
  }

  private static checkInputs(cacheItem: ICacheItem, inputs: readonly any[], inputsEqualFn: InputsEqualFn | undefined): any {
    if (cacheItem && inputsEqualFn && inputsEqualFn(cacheItem.inputs, inputs)) {
      cacheItem.stats.argumentsEqual++;
      return cacheItem.result;
    }
    return nothingSym;
  }

  static logCacheItem(name: string, inputNew: readonly any[], resultNew: any, last: ICacheItem | undefined): void {
    console.log("===============================================")
    console.log(`Cache ${name} different result`);
    console.log("-----------------------------------------------")
    console.log("Input new", inputNew);
    console.log("Input old", last?.inputs);
    console.log("Result new ", resultNew);
    console.log("Result old ", last?.result);
    console.log("===============================================")
  }

  static incUsage(id: string, usages: ICacheUsages): number {
    const count = (usages[id] || 0) + 1;
    usages[id] = count;
    if (count > 100)
      throw new Error("Safety Check...");
    // console.log("Inc ", id, count);
    return count;
  }

  static deleteUsageItem = (id: string, usages: ICacheUsages, cache: Map<string, ICacheItem>): void => {
    // console.log("Delete ", id);
    delete usages[id];
    cache.delete(id);
  }

  static decUsage(id: string, usages: ICacheUsages, cacheForDeletion?: Map<string, ICacheItem>): number {
    const count = usages[id] - 1;
    usages[id] = count;
    // console.log("Dec ", id, count);

    if (count === 0 && cacheForDeletion)
      CacheUtil.deleteUsageItem(id, usages, cacheForDeletion);
    return count;
  }

  // const deleteEmpty = (usages: Record<string, number>, cache: Record<string, IMemoizeState>): void => {
//   const keys = Object.keys(usages);
//   for (const k of keys) {
//     if (usages[k] === 0) {
//       deleteItem(k, usages, cache);
//     }
//   }
// }
}


export function cached<T>(name: string, cacheObject: Record<string, T>, t: () => T): T {
  const tc = cacheObject[name]
  if (tc)
    return tc;
  const newT = t();
  cacheObject[name] = newT;
  return newT;
}